import { jsx, jsxs } from "react/jsx-runtime";
import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import * as React from "react";
import { Manager, Reference, Popper } from "react-popper";
import { Portal } from "react-portal";
import { CSSTransition } from "react-transition-group";
import { ConfigContext } from "../../../../../../contexts/config-context.js";
import { filterDataProps } from "../../utils/FilterDataProps.js";
import { classes, st } from "./CorePopover.st.css";
import { CorePopoverContext } from "./CorePopoverContext.js";
import { buildChildrenObject } from "./utils/buildChildrenObject.js";
import { createComponentThatRendersItsChildren } from "./utils/createComponentThatRendersItsChildren.js";
import { getAppendToElement } from "./utils/getAppendToElement.js";
import { createModifiers } from "./utils/modifiers.js";
import { CoreClickOutside } from "../CoreClickOutside/CoreClickOutside.js";
import { FormTheme } from "../../../../../../theme/form-theme.js";
import { getStyle } from "../../../../../../theme/get-style.js";
import { FormStyles } from "../../../../../../theme/form-styles.js";
import { StyleProvider } from "../../../../../../theme/style-provider.js";
let testId;
const isTestEnv = false;
const attachClasses = (node, classnames) => node && node.classList.add(...classnames.split(" "));
const detachClasses = (node, classnames) => node && node.classList.remove(...classnames.split(" "));
const shouldAnimatePopover = ({ timeout }) => {
  if (typeof timeout === "object") {
    const { enter, exit } = timeout;
    return typeof enter !== "undefined" && typeof exit !== "undefined" && (enter > 0 || exit > 0);
  }
  return !!timeout;
};
const getArrowShift = (shift, direction) => {
  if (!shift && !isTestEnv) {
    return {};
  }
  if (direction.startsWith("top") || direction.startsWith("bottom")) {
    return { left: `${shift}px` };
  }
  if (direction.startsWith("left") || direction.startsWith("right")) {
    return { top: `${shift}px` };
  }
  return {};
};
class CorePopover extends React.Component {
  static displayName = "CorePopover";
  static defaultProps = {
    flip: true,
    fixed: false,
    zIndex: "var(--portals-z-index, 1000)"
  };
  static Element = createComponentThatRendersItsChildren("Popover.Element");
  static Content = createComponentThatRendersItsChildren("Popover.Content");
  targetRef = null;
  portalNode = null;
  portalClasses;
  appendToNode = null;
  clickOutsideRef = null;
  popoverContentRef;
  clickOutsideClass;
  contentHook;
  popperScheduleUpdate = null;
  // Timer instances for the show/hide delays
  _hideTimeout = null;
  _showTimeout = null;
  constructor(props) {
    super(props);
    this.state = {
      isMounted: false,
      shown: props.shown || false
    };
    this.clickOutsideRef = React.createRef();
    this.popoverContentRef = React.createRef();
    this.clickOutsideClass = uniqueId("clickOutside");
    this.contentHook = `popover-content-${props["data-hook"] || ""}-${testId}`;
  }
  _handleClickOutside = (event) => {
    const {
      onClickOutside: onClickOutsideCallback,
      shown,
      disableClickOutsideWhenClosed
    } = this.props;
    if (onClickOutsideCallback && !(disableClickOutsideWhenClosed && !shown)) {
      onClickOutsideCallback(event);
    }
  };
  _onKeyDown = (e) => {
    const { onEscPress } = this.props;
    if (onEscPress && e.key === "Escape") {
      onEscPress(e);
    }
  };
  focus() {
    if (this.popoverContentRef.current) {
      this.popoverContentRef.current.focus();
    }
  }
  getPopperContentStructure(childrenObject) {
    const { shown } = this.state;
    const {
      moveBy,
      appendTo,
      placement,
      showArrow,
      moveArrowTo,
      flip,
      fixed,
      customArrow,
      role,
      id,
      zIndex,
      minWidth,
      maxWidth,
      width,
      dynamicWidth,
      excludeClass = this.clickOutsideClass,
      contentClassName,
      onEscPress,
      tabIndex,
      eventsEnabled = true,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      "aria-describedby": ariaDescribedBy
    } = this.props;
    const shouldAnimate = shouldAnimatePopover(this.props);
    const modifiers = createModifiers({
      minWidth,
      width,
      dynamicWidth,
      moveBy,
      appendTo,
      shouldAnimate,
      flip,
      placement,
      fixed,
      isTestEnv
    });
    const popper = /* @__PURE__ */ jsx(
      Popper,
      {
        modifiers,
        placement,
        eventsEnabled,
        children: ({
          ref,
          style: popperStyles,
          placement: popperPlacement,
          arrowProps,
          scheduleUpdate,
          disableEventListeners
        }) => {
          this.popperScheduleUpdate = eventsEnabled ? scheduleUpdate : void 0;
          return /* @__PURE__ */ jsx(ConfigContext.Consumer, { children: (config) => /* @__PURE__ */ jsx(FormTheme, { style: getStyle(config?.style), children: /* @__PURE__ */ jsx(FormStyles, { enabled: Boolean(config?.style), children: /* @__PURE__ */ jsx(StyleProvider, { children: /* @__PURE__ */ jsx(CorePopoverContext.Consumer, { children: ({ excludeClickOutsideClasses }) => {
            return /* @__PURE__ */ jsxs(
              "div",
              {
                ref,
                "data-hook": "popover-content",
                "data-content-element": this.contentHook,
                style: { ...popperStyles, zIndex, maxWidth },
                "data-placement": popperPlacement || placement,
                className: classNames(
                  classes.popover,
                  this.clickOutsideClass,
                  contentClassName,
                  {
                    [classes.withArrow]: showArrow,
                    [classes.popoverContent]: !showArrow
                  },
                  ...excludeClickOutsideClasses
                ),
                children: [
                  showArrow && this.renderArrow(
                    arrowProps,
                    moveArrowTo,
                    popperPlacement || placement,
                    customArrow
                  ),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      id,
                      role,
                      tabIndex,
                      ref: this.popoverContentRef,
                      className: showArrow ? classes.popoverContent : "",
                      onKeyDown: shown && onEscPress ? this._onKeyDown : void 0,
                      "aria-label": ariaLabel,
                      "aria-labelledby": ariaLabelledby,
                      "aria-describedby": ariaDescribedBy,
                      children: /* @__PURE__ */ jsx(
                        CorePopoverContext.Provider,
                        {
                          value: {
                            excludeClickOutsideClasses: [
                              excludeClass,
                              ...excludeClickOutsideClasses
                            ]
                          },
                          children: childrenObject.Content
                        }
                      )
                    },
                    "popover-content"
                  )
                ]
              }
            );
          } }) }) }) }) });
        }
      }
    );
    return this.wrapWithAnimations(popper);
  }
  applyStylesToPortaledNode() {
    const { shown } = this.state;
    const shouldAnimate = shouldAnimatePopover(this.props);
    if (shouldAnimate || shown) {
      attachClasses(this.portalNode, this.portalClasses);
    } else {
      detachClasses(this.portalNode, this.portalClasses);
    }
  }
  wrapWithAnimations(popper) {
    const { timeout } = this.props;
    const { shown } = this.state;
    const shouldAnimate = shouldAnimatePopover(this.props);
    return shouldAnimate ? /* @__PURE__ */ jsx(
      CSSTransition,
      {
        in: shown,
        timeout,
        unmountOnExit: true,
        classNames: {
          enter: classes["popoverAnimation-enter"],
          enterActive: classes["popoverAnimation-enter-active"],
          exit: classes["popoverAnimation-exit"],
          exitActive: classes["popoverAnimation-exit-active"]
        },
        onExited: () => detachClasses(this.portalNode, this.portalClasses),
        children: popper
      }
    ) : popper;
  }
  renderPopperContent(childrenObject) {
    const popper = this.getPopperContentStructure(childrenObject);
    return this.portalNode ? /* @__PURE__ */ jsx(Portal, { node: this.portalNode, children: popper }) : popper;
  }
  renderArrow(arrowProps, moveArrowTo, placement, customArrow) {
    const commonProps = {
      ref: arrowProps.ref,
      key: "popover-arrow",
      "data-hook": "popover-arrow",
      style: {
        ...arrowProps.style,
        ...getArrowShift(moveArrowTo, placement)
      }
    };
    if (customArrow) {
      return customArrow(placement, commonProps);
    }
    return /* @__PURE__ */ jsx("div", { ...commonProps, className: classes.arrow });
  }
  componentDidMount() {
    const { shown, onTabOut } = this.props;
    this.initAppendToNode();
    if (onTabOut && shown) {
      this._setBlurByKeyboardListener();
    }
    this.setState({ isMounted: true });
  }
  /**
   * Checks to see if the focused element is outside the Popover content
   */
  _onDocumentKeyUp = (e) => {
    const { onTabOut } = this.props;
    if (typeof document !== "undefined" && this.popoverContentRef.current && !this.popoverContentRef.current.contains(document.activeElement)) {
      onTabOut(e);
    }
  };
  _setBlurByKeyboardListener() {
    if (typeof document !== "undefined") {
      document.addEventListener("keyup", this._onDocumentKeyUp, true);
    }
  }
  _removeBlurListener() {
    if (typeof document !== "undefined") {
      document.removeEventListener("keyup", this._onDocumentKeyUp, true);
    }
  }
  initAppendToNode() {
    const { appendTo } = this.props;
    this.appendToNode = getAppendToElement(appendTo, this.targetRef);
    if (this.appendToNode) {
      this.portalNode = document.createElement("div");
      this.portalNode.setAttribute("data-hook", "popover-portal");
      Object.assign(this.portalNode.style, {
        position: "static",
        top: 0,
        left: 0,
        width: 0,
        height: 0
      });
      this.appendToNode.appendChild(this.portalNode);
    }
  }
  hidePopover() {
    const { isMounted } = this.state;
    const { hideDelay, onTabOut } = this.props;
    if (!isMounted || this._hideTimeout) {
      return;
    }
    if (this._showTimeout) {
      clearTimeout(this._showTimeout);
      this._showTimeout = null;
    }
    if (onTabOut) {
      this._removeBlurListener();
    }
    if (hideDelay) {
      this._hideTimeout = setTimeout(() => {
        this.setState({ shown: false });
      }, hideDelay);
    } else {
      this.setState({ shown: false });
    }
  }
  showPopover() {
    const { isMounted } = this.state;
    const { showDelay, onTabOut } = this.props;
    if (!isMounted || this._showTimeout) {
      return;
    }
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
    if (onTabOut) {
      this._setBlurByKeyboardListener();
    }
    if (showDelay) {
      this._showTimeout = setTimeout(() => {
        this.setState({ shown: true });
      }, showDelay);
    } else {
      this.setState({ shown: true });
    }
  }
  componentWillUnmount() {
    if (this.portalNode && this.appendToNode.children.length) {
      this.appendToNode.removeChild(this.portalNode);
    }
    this.portalNode = null;
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
    if (this._showTimeout) {
      clearTimeout(this._showTimeout);
      this._showTimeout = null;
    }
  }
  updatePosition() {
    if (this.popperScheduleUpdate) {
      this.popperScheduleUpdate();
    }
  }
  componentDidUpdate(prevProps) {
    const { shown } = this.props;
    if (this.portalNode) {
      this.portalClasses = st(classes.root, this.props.className);
      this.applyStylesToPortaledNode();
    }
    if (prevProps.shown !== shown) {
      if (shown) {
        this.showPopover();
      } else {
        this.hidePopover();
      }
    } else {
      this.updatePosition();
    }
  }
  render() {
    const {
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onClick,
      children,
      style,
      id,
      excludeClass,
      fluid
    } = this.props;
    const { isMounted, shown } = this.state;
    const childrenObject = buildChildrenObject(children, {
      Element: null,
      Content: null
    });
    const shouldAnimate = shouldAnimatePopover(this.props);
    const shouldRenderPopper = isMounted && (shouldAnimate || shown);
    return /* @__PURE__ */ jsx(Manager, { children: /* @__PURE__ */ jsx(
      CoreClickOutside,
      {
        rootRef: this.clickOutsideRef,
        onClickOutside: shown ? this._handleClickOutside : void 0,
        excludeClass: [this.clickOutsideClass, excludeClass],
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            ref: this.clickOutsideRef,
            style,
            "data-content-hook": this.contentHook,
            className: st(classes.root, { fluid }, this.props.className),
            onMouseEnter,
            onMouseLeave,
            id,
            ...filterDataProps(this.props),
            children: [
              /* @__PURE__ */ jsx(Reference, { innerRef: (r) => this.targetRef = r, children: ({ ref }) => /* @__PURE__ */ jsx(
                "div",
                {
                  ref,
                  className: classes.popoverElement,
                  "data-hook": "popover-element",
                  onClick,
                  onKeyDown,
                  children: childrenObject.Element
                }
              ) }),
              shouldRenderPopper && this.renderPopperContent(childrenObject)
            ]
          }
        )
      }
    ) });
  }
}
export {
  CorePopover
};
//# sourceMappingURL=CorePopover.js.map
